
import Experience from '@/models/Experience';
import Goal from '@/models/Goal';
import GoalPageModel from '@/models/GoalPageModel';
import { Component, Vue, Ref } from 'vue-property-decorator';
import CustomWindow from '../CustomWindow';
import NEDLogoSvg from '../svg/ned-navigator-logo.svg';
import StepsNavLineSvg from '../svg/steps-nav-line.svg';
import BoxDashedSvg from '../svg/box-dashed-gradient.svg';
import WaveSmallSvg from '../svg/wave-small.svg';
import ShipSmallSvg from '../svg/ship-small.svg';
import NedDocumentApi from '@/services/nedDocumentApi';

declare const window: CustomWindow;

@Component({
    components: {
        NEDLogoSvg,
        StepsNavLineSvg,
        BoxDashedSvg,
        WaveSmallSvg,
        ShipSmallSvg
    }
})
export default class NedNavigator extends Vue {
    @Ref('nedNavigatorInner')
    readonly nedNavigatorInner!: HTMLElement;
    private nedDocumentApi = new NedDocumentApi();
    private isReady = false;

    updated() {
        if (this.model && !this.isReady) {
            this.updateScrollPaddingNow();
            this.updateNedWindowButtonPositionNow();
            this.showWindowOnScroll();
            this.isReady = true;
        }
    }

    get isNavigatorEnabled(): boolean {
        return !document.body.classList.contains('goal-window-disabled');
    }

    created() {
        if (this.isNavigatorEnabled) {
            window.addEventListener("resize", this.resizeEventHandler);
            window.addEventListener('scroll', this.scrollEventHandler);
        }
    }

    destroyed() {
        if (this.isNavigatorEnabled) {
            window.removeEventListener("resize", this.resizeEventHandler);
            window.removeEventListener('scroll', this.scrollEventHandler);
        }
    }

    resizeEventHandler() {
        this.updateScrollPadding();
        this.updateNedWindowButtonPosition();
        this.showWindowOnScroll();
    }

    scrollEventHandler() {
        this.updateNedWindowButtonPosition();
    }

    showWindowOnScroll() {
        const scrollPosition = document ? document.documentElement.scrollTop : 0;
        // Auto open goal window, when user scrolled to Bundle section.
        if (scrollPosition > this.nedDocumentApi.getHeightContentBeforeBundles()) {
            const isGoalWindowClosed = localStorage.getItem('isGoalWindowClosed');
            const isGoalWindowClosedBefore = isGoalWindowClosed && isGoalWindowClosed === "true";
            if (!isGoalWindowClosedBefore) {
                this.$store.commit('setNedWindowIsOpen', true);
            }
        }
    }

    private positionTimer: number | null = null;

    updateNedWindowButtonPosition() {
        if (this.positionTimer) {
            clearTimeout(this.positionTimer);
            this.positionTimer = null;
        }
        this.positionTimer = setTimeout(() => {
            this.updateNedWindowButtonPositionNow();
        }, 250);
    }

    updateNedWindowButtonPositionNow() {
        this.$store.commit('setNedWindowButtonPositionRight', this.nedDocumentApi.calcPositionForGoalButton());
        this.$store.commit('setScrollWidth', this.nedDocumentApi.getBodyPaddingRightPx());
    }

    private resizeTimer: number | null = null;

    updateScrollPadding() {
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = null;
        }
        this.resizeTimer = setTimeout(() => {
            this.updateScrollPaddingNow();
        }, 250);
    }

    updateScrollPaddingNow() {
        if (this.$store.getters.getNedWindowIsOpen) {
            this.nedDocumentApi.addPaddingForScroll();
        } else {
            this.nedDocumentApi.removePaddingForScroll();
        }
    }

    openCloseWindow() {
        this.$store.commit('setNedWindowIsOpen', !this.$store.getters.getNedWindowIsOpen);
    }

    showMore() {
        const goalsPage = `${this.model?.siteUrl}goals`;
        if (window.location.href === goalsPage) {
            this.openCloseWindow();
        } else {
            window.location.href = goalsPage;
        }
    }

    selectGoal(goal: number) {
        if (goal !== this.goal) {
            this.$store.dispatch('saveGoal', goal);
            const goalName = this.$store.getters.convertGoalToString(goal);
            window.goalCoreInstance.changeContentOnGoal(goalName, this.nedDocumentApi.getHeightContentBeforeBundles());
        }
        setTimeout(() => {
            this.setStep(1);
        }, 100);
    }

    selectExperience(experience: number) {
        if (experience !== this.experience) {
            this.$store.dispatch('saveExperience', experience);
        }
        setTimeout(() => {
            this.setStep(2);
        }, 100);
    }

    private isStepChanging = false;

    setStep(step: number) {
        let isChanging = false;
        if (step < this.step) {
            this.$store.commit('setNedWizardBack', true);
            this.$store.commit('setNedWizardStep', step);
            isChanging = true;
        }
        else if (step > this.step && (step === 0 || step === 1 && this.goal || step === 2 && this.goal && this.experience)) { 
            this.$store.commit('setNedWizardBack', false);           
            this.$store.commit('setNedWizardStep', step);
            isChanging = true;
        }
        if (isChanging) {
            this.isStepChanging = true;
            setTimeout(() => {
                this.isStepChanging = false;
            }, 750);
            setTimeout(() => {
                this.nedNavigatorInner.scrollTo({ top: 0, behavior: 'smooth' });
            }, 760);
        }
    }

    get hideButton(): boolean {
        return this.$store.getters.getHideNedButton;
    }

    get nedWindowIsOpen(): string {
        return this.$store.getters.getNedWindowIsOpen;
    }

    get buttonPosition(): string {
        return this.$store.getters.getNedWindowButtonPositionRight;
    }

    get scrollWidth(): string {
        return this.$store.getters.getScrollWidth;
    }

    get wizardBack(): boolean {
        return this.$store.getters.getNedWizardBack;
    }

    get step(): number {
        return this.$store.getters.getNedWizardStep;
    }

    get model(): GoalPageModel | null {
        return this.$store.getters.getGoalsInfo;
    }
    
    get isInitializedGoalsInfo(): boolean {
        return this.$store.getters.getInitializedGoalsInfo;
    }    

    get goal(): number {
        return this.$store.getters.getGoal;
    }

    get experience(): number {
        return this.$store.getters.getExperience;
    }
    
    get isInitializedGoal(): boolean {
        return this.$store.getters.getInitializedGoal;
    }
    
    get isInitializedExperience(): boolean {
        return this.$store.getters.getInitializedExperience;
    }

    get currentGoalModel(): Goal | null {
        if (this.$store.getters.getInitializedGoal && this.$store.getters.getInitializedGoalsInfo &&
            this.$store.getters.getGoalsInfo && this.$store.getters.getGoalsInfo.goals) {
            const goalCode = this.$store.getters.getGoal as number;
            const goalsPageModel = this.$store.getters.getGoalsInfo as GoalPageModel;
            const goals = goalsPageModel.goals.filter(x => x.code === goalCode);
            if (goals.length > 0) {
                return goals[0];
            }
        }
        return null;
    }

    get currentExperienceModel(): Experience | null {
        const currentGoal = this.currentGoalModel;
        if (currentGoal && this.$store.getters.getInitializedExperience) {
            const id = this.$store.getters.getExperience as number;
            const exps = currentGoal.experiences.filter(x => x.id === id);
            if (exps.length > 0) {
                return exps[0];
            }
        }
        return null;
    }
}
